<script lang="ts">
  // The main screen: sidebar, conversation and, on the right, a thread or the pinned
  // messages. From 1,100 px the three columns sit side by side; between 760 and 1,100 px
  // the panel covers the conversation; below 760 px each takes the whole screen
  // (docs/spec.md, Interface web).
  import { untrack } from 'svelte';
  import { conversationPath, router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import { signalActivity } from '../lib/ws';
  import ConversationView from './ConversationView.svelte';
  import NewConversation from './NewConversation.svelte';
  import FilePreview from './FilePreview.svelte';
  import PinnedPanel from './PinnedPanel.svelte';
  import AdminView from './AdminView.svelte';
  import MembersPanel from './MembersPanel.svelte';
  import MyTasksView from './MyTasksView.svelte';
  import SavedView from './SavedView.svelte';
  import ProfileView from './ProfileView.svelte';
  import SecurityView from './SecurityView.svelte';
  import SearchView from './SearchView.svelte';
  import Sidebar from './Sidebar.svelte';
  import ThreadPanel from './ThreadPanel.svelte';

  let { onLogout }: { onLogout: () => void } = $props();

  let creating = $state(false);
  let pinnedOpen = $state(false);
  let membersOpen = $state(false);

  const LAST = 'last-conversation';
  function remember(id: string): void {
    try {
      localStorage.setItem(LAST, id);
    } catch {
      // Not kept in private browsing: « / » then opens the most recent conversation.
    }
  }
  function lastOpened(): string | null {
    try {
      return localStorage.getItem(LAST);
    } catch {
      return null;
    }
  }

  const route = $derived(router.route);
  const routeId = $derived(route.name === 'conversation' || route.name === 'files' || route.name === 'board' ? route.id : null);
  const threadId = $derived(route.name === 'conversation' ? route.thread : null);
  // A reply is shown in its thread: the conversation centres on the root.
  const focus = $derived(route.name === 'conversation' && route.focus ? (route.thread ?? route.focus) : null);
  const current = $derived(app.conversation(routeId));
  const preview = $derived(app.preview && app.preview.conversation_id === routeId && route.name === 'conversation' ? app.preview : null);
  // A preview opened from a thread or a task shows over it; closing it comes back.
  const panel = $derived<'thread' | 'pinned' | 'preview' | 'members' | null>(
    preview ? 'preview' : threadId ? 'thread' : membersOpen ? 'members' : pinnedOpen ? 'pinned' : null,
  );
  const wide = typeof matchMedia === 'function' ? matchMedia('(min-width: 760px)') : null;

  // « / » on a computer: the last conversation opened (or the most recent one).
  $effect(() => {
    if (route.name !== 'home' || !wide?.matches || app.conversations.length === 0) return;
    const last = lastOpened();
    const target = app.conversation(last) ?? app.conversations.find((c) => !c.archived_at);
    if (target) router.go(conversationPath(target.id), { replace: true });
  });

  $effect(() => {
    app.openId = current?.id ?? null;
    if (current) remember(current.id);
    signalActivity();
  });

  // The pinned panel belongs to one conversation; a thread replaces it.
  $effect(() => {
    void routeId;
    pinnedOpen = false;
    membersOpen = false;
  });
  // The members panel also shows over the board and the files; the others belong to the
  // discussion.
  const panelShown = $derived(!!panel && !!current && (route.name === 'conversation' || panel === 'members'));

  // Opening another thread closes a preview.
  $effect(() => {
    void threadId;
    untrack(() => (app.preview = null));
  });

  function closePanel() {
    if (app.preview) {
      app.preview = null;
      return;
    }
    pinnedOpen = false;
    membersOpen = false;
    if (threadId && current) router.go(conversationPath(current.id));
  }

  function toggleMembers() {
    app.preview = null;
    pinnedOpen = false;
    if (threadId && current) router.go(conversationPath(current.id));
    membersOpen = !membersOpen || !!threadId;
  }

  function togglePinned() {
    app.preview = null;
    membersOpen = false;
    if (threadId && current) router.go(conversationPath(current.id));
    pinnedOpen = !pinnedOpen || !!threadId;
  }

  // Ctrl K (⌘K): the search, from anywhere.
  function onKey(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (route.name !== 'search') router.go('/search');
    }
  }
</script>

<svelte:window onkeydown={onKey} />

<div
  class="shell"
  class:in-conversation={!!routeId || ['saved', 'search', 'security', 'admin', 'profile', 'tasks'].includes(route.name)}
  class:with-panel={panelShown}
>
  <div class="side">
    <Sidebar onNew={() => (creating = true)} {onLogout} />
  </div>
  <main class="main">
    {#if route.name === 'saved'}
      <SavedView />
    {:else if route.name === 'search'}
      <SearchView {route} />
    {:else if route.name === 'security'}
      <SecurityView />
    {:else if route.name === 'profile'}
      <ProfileView />
    {:else if route.name === 'tasks'}
      <MyTasksView />
    {:else if route.name === 'admin'}
      <AdminView />
    {:else if current}
      <ConversationView
        conversation={current}
        {focus}
        tab={route.name === 'files' ? 'files' : route.name === 'board' ? 'board' : 'discussion'}
        fileId={route.name === 'files' ? route.file : null}
        pinnedOpen={panel === 'pinned'}
        onTogglePinned={togglePinned}
        membersOpen={panel === 'members'}
        onToggleMembers={toggleMembers}
      />
    {:else if routeId}
      <p class="empty">Conversation introuvable, ou vous n'en êtes plus membre.</p>
    {:else}
      <p class="empty">Choisissez une conversation.</p>
    {/if}
  </main>
  {#if panelShown && current}
    <div class="panel">
      {#if panel === 'thread' && threadId}
        <ThreadPanel conversation={current} rootId={threadId} onClose={closePanel} />
      {:else if panel === 'members'}
        <MembersPanel conversation={current} onClose={closePanel} />
      {:else if panel === 'preview' && preview}
        <aside class="preview-panel" aria-label="Aperçu du fichier">
          <button type="button" class="close" aria-label="Fermer l'aperçu" onclick={closePanel}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          {#key preview.id}<FilePreview file={preview} compact />{/key}
        </aside>
      {:else}
        <PinnedPanel conversation={current} onClose={closePanel} />
      {/if}
    </div>
  {/if}
</div>

{#if creating}
  <NewConversation onClose={() => (creating = false)} />
{/if}

<style>
  .shell {
    position: relative;
    height: 100%;
    display: grid;
    grid-template-columns: var(--sidebar-w) minmax(0, 1fr);
  }
  .shell.with-panel {
    grid-template-columns: var(--sidebar-w) minmax(0, 1fr) var(--thread-w);
  }
  .side,
  .main,
  .panel {
    min-height: 0;
    height: 100%;
  }
  .shell.with-panel:has(.preview-panel) {
    grid-template-columns: var(--sidebar-w) minmax(0, 1fr) minmax(var(--thread-w), 42%);
  }
  .preview-panel {
    position: relative;
    height: 100%;
    border-left: 1px solid var(--border);
    background: var(--bg);
  }
  .preview-panel .close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: var(--radius-s);
    background: var(--bg);
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .preview-panel :global(header) {
    padding-right: 48px;
  }
  .empty {
    margin: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--muted);
  }
  /* Between 760 and 1,100 px: the panel covers the conversation. */
  @media (max-width: 1099px) {
    .shell.with-panel {
      grid-template-columns: var(--sidebar-w) minmax(0, 1fr);
    }
    .panel {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: min(var(--thread-w), 100%);
      z-index: 15;
      box-shadow: -8px 0 24px rgba(16, 24, 40, 0.12);
    }
  }
  @media (max-width: 759px) {
    .shell,
    .shell.with-panel {
      grid-template-columns: minmax(0, 1fr);
    }
    .shell.in-conversation .side,
    .shell:not(.in-conversation) .main {
      display: none;
    }
    .panel {
      width: 100%;
      box-shadow: none;
    }
  }
</style>
